import type { MouseEvent } from 'react';
import { mergeProps, useFocusRing, usePress } from 'react-aria';
import { NavLink, useLocation, matchPath } from 'react-router-dom';

import { Glyph } from '../../common/Glyph';

import { useSetNavContextState } from '../../../containers/NavContext';

import { type GlyphType } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';
import styles from './MainNavItem.module.css';

type SystemGlyph = GlyphType['system'][keyof GlyphType['system']];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DynamicPage = ((...args: any[]) => any) & { preload?: () => void };

interface MainNavItemProps {
  glyphSource: SystemGlyph;
  to: string;
  label: string;
  dynamic?: DynamicPage;
  exact?: boolean;
  isActive?: (match: unknown, location: unknown) => boolean;
}

export function MainNavItem(props: MainNavItemProps) {
  const location = useLocation();
  const match = matchPath(location.pathname, { path: props.to, exact: props.exact });
  const isActive = props.isActive != null ? props.isActive(match, location) : match != null;
  const { toggleNavCollapsed } = useSetNavContextState();

  const { pressProps } = usePress({
    onPress() {
      if (isActive) {
        toggleNavCollapsed();
      }
    },
  });

  const focusRing = useFocusRing();

  return (
    <NavLink
      {...mergeProps(pressProps, focusRing.focusProps, {
        onMouseEnter: () => props.dynamic?.preload?.(),
        onPointerEnter: () => props.dynamic?.preload?.(),
        // Prevent same-page navigation when active — usePress onPress can't preventDefault on the native event.
        onClick(event: MouseEvent) {
          if (isActive) {
            event.preventDefault();
          }
        },
      })}
      className={styles.root}
      exact={props.exact}
      to={props.to}
      data-is-active={isActive}
      data-is-focus-visible={focusRing.isFocusVisible}
      aria-label={props.label}
    >
      <span className={styles.iconWrapper}>
        <Glyph
          className={styles.moduleGlyph}
          source={props.glyphSource}
          size={theme.icon.size_xlarge}
          color={'var(--module-glyph-color)'}
        />
      </span>
      <span className={styles.label}>
        {props.label === 'HomeyScript' ? (
          <>
            Homey
            <wbr />
            Script
          </>
        ) : (
          props.label
        )}
      </span>
    </NavLink>
  );
}
